import axios, { AxiosError } from 'axios';
import { isEmpty, isNil } from 'rambdax';
import { Hydrate, QueryClient, QueryClientProvider, type DehydratedState } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';

import { ApiErrorResponse } from '@ping/api';
import type { ErrorType } from '@ping/api/custom-instance';
import { t } from '@ping/helpers';
import { Toast } from '@ping/uikit';

export interface IReactQueryProviderProps {
  dehydratedState?: DehydratedState;
  children: React.ReactNode;
}

const TraderApiErrorCodes = {
  ExceedingPerTransactionWithdrawalLimit: 70100,
} as const;

//
// NOTE: If an error code is handled in other places via a non-toast method, e.g. dialog, it should be added here.
const CoveredErrorCodes = new Set<number>([TraderApiErrorCodes.ExceedingPerTransactionWithdrawalLimit]);

/** Per-toast override so validation / API errors dismiss without leaving `autoClose: false` stuck open */
const ERROR_TOAST_AUTO_CLOSE_MS = 8000;

function isNetworkException(error: ErrorType<any>): error is AxiosError & { code: 'ERR_NETWORK' } {
  return axios.isAxiosError(error) && error.code === 'ERR_NETWORK';
}

/** Non-JSON bodies (e.g. Next.js HTML error pages) must never be rendered as toast content. */
function isNonApiErrorPayload(data: unknown): boolean {
  if (typeof data === 'string') {
    const trimmed = data.trimStart();
    return trimmed.startsWith('<!DOCTYPE') || trimmed.startsWith('<html') || trimmed.startsWith('<');
  }
  return false;
}

function isKybDemoPath(): boolean {
  return typeof window !== 'undefined' && window.location.pathname.startsWith('/kyb');
}

export function handleApiQueryError(error: ErrorType<ApiErrorResponse>) {
  handleOnRequestError(error);
}

function handleOnRequestError(error: ErrorType<ApiErrorResponse>) {
  // Mock KYB demo must stay free of live-API error toasts during presentations.
  if (isKybDemoPath()) {
    return;
  }

  const { data } = error.response ?? {};
  if (isNil(data)) {
    if (isNetworkException(error)) {
      Toast.error({
        title: t('Network error'),
        text: t('Please check your internet connection and try again'),
        options: { toastId: error.code, autoClose: ERROR_TOAST_AUTO_CLOSE_MS },
      });
    }
    return;
  }

  if (isNonApiErrorPayload(data)) {
    return;
  }

  const errors = data.errors;
  if (Array.isArray(errors) && !isEmpty(errors)) {
    const text = errors.map(err => `- ${err?.message ?? String(err)}`).join('\n');
    Toast.error({
      title: data.message,
      text,
      options: {
        autoClose: ERROR_TOAST_AUTO_CLOSE_MS,
        toastId: `${data.errorCode ?? error.code}-${text}`,
      },
    });
    return;
  }

  if (!CoveredErrorCodes.has(data.errorCode)) {
    Toast.error({
      title: data.message ?? data,
      options: { toastId: data.errorCode ?? error.code, autoClose: ERROR_TOAST_AUTO_CLOSE_MS },
    });
  }
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: ErrorType<any>) => {
        if (axios.isCancel(error)) {
          return false;
        }
        return failureCount < 3;
      },
      // we don't need to refetch on every focus. that's just unnecessary stress on server maybe add it later again
      refetchOnWindowFocus: false,
      onError: handleOnRequestError,
    },
    mutations: {
      onError: handleOnRequestError,
    },
  },
});

const ReactQueryProvider = ({ children, dehydratedState }: IReactQueryProviderProps) => {
  return (
    <QueryClientProvider client={queryClient}>
      <Hydrate state={dehydratedState}>{children}</Hydrate>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
};

export default ReactQueryProvider;
